import type {
  DeliveryStat,
  NotificationEventRow,
  ZbsReadiness,
} from "../../shared/api/notificationSettings";

/**
 * What the settings screen says about each event's health (#19566) — the configuration's
 * prediction (readiness per channel) and the outcome (the last days' delivery counts), reduced to
 * one verdict per event so the top of the page can say "2 thông báo cần xử lý".
 */

/** States that mean a channel is not meant to send at all — nothing to warn about. */
const QUIET: ZbsReadiness[] = ["READY", "DISABLED", "CHANNEL_OFF", "UNKNOWN"];

export interface EventTotals {
  /** The provider took it: SENT (an SMTP server or SMS gateway accepted it) or DELIVERED. */
  ok: number;
  /**
   * Not answered yet: QUEUED in HIS, or ACCEPTED by Addy Message with no outcome reported back.
   * #19642 — ACCEPTED used to count as "đã gửi", so every e-mail read as sent while no status had
   * ever come back (and while no server even existed). It is a wait, not a success.
   */
  queued: number;
  failed: number;
  notSent: number;
  expired: number;
  /** Reasons, per channel — an e-mail refusal and a Zalo refusal are fixed in different places. */
  failures: { code: string; channel: string; count: number }[];
  /**
   * #19667 D6 — the same counts split by channel, in ZBS / SMS / EMAIL order. The total alone could
   * not say whether "9 lỗi" were SMS or e-mail, which is the first thing anyone fixing it needs.
   */
  byChannel: { channel: string; ok: number; queued: number; failed: number }[];
}

/**
 * The log writes "ZNS" where configuration says "ZBS" (#19667 D5): one channel under two keys
 * would split its numbers in two, so they are counted under one name.
 */
export function canonicalChannel(channel: string): string {
  return channel === "ZNS" ? "ZBS" : channel;
}

const CHANNEL_ORDER = ["ZBS", "SMS", "EMAIL"];

/** One event's counts over every channel (the log says "ZNS" where configuration says "ZBS"). */
export function totalsFor(eventKey: string, stats: DeliveryStat[]): EventTotals {
  const t: EventTotals = {
    ok: 0,
    queued: 0,
    failed: 0,
    notSent: 0,
    expired: 0,
    failures: [],
    byChannel: [],
  };
  const byCode = new Map<string, { code: string; channel: string; count: number }>();
  const perChannel = new Map<string, { channel: string; ok: number; queued: number; failed: number }>();
  for (const s of stats) {
    if (s.eventKey !== eventKey) continue;
    const channel = canonicalChannel(s.channel);
    const ch = perChannel.get(channel) ?? { channel, ok: 0, queued: 0, failed: 0 };
    perChannel.set(channel, ch);
    for (const [status, n] of Object.entries(s.counts)) {
      if (status === "SENT" || status === "DELIVERED") {
        t.ok += n;
        ch.ok += n;
      } else if (status === "QUEUED" || status === "ACCEPTED") {
        t.queued += n;
        ch.queued += n;
      } else if (status === "REJECTED" || status === "FAILED") {
        t.failed += n;
        ch.failed += n;
      } else if (status === "UNDELIVERABLE") {
        t.notSent += n;
        ch.failed += n;
      } else if (status === "EXPIRED") t.expired += n;
    }
    for (const f of s.failures) {
      const key = channel + "|" + f.code;
      const cur = byCode.get(key) ?? { code: f.code, channel, count: 0 };
      cur.count += f.count;
      byCode.set(key, cur);
    }
  }
  t.failures = [...byCode.values()].sort((a, b) => b.count - a.count);
  t.byChannel = [...perChannel.values()].sort(
    (a, b) => CHANNEL_ORDER.indexOf(a.channel) - CHANNEL_ORDER.indexOf(b.channel),
  );
  return t;
}

export type Verdict = "ok" | "attention" | "broken" | "off";

/**
 * - `off`: switched off — nothing to say.
 * - `broken`: switched on, and no channel can send it (or the Zalo template is about something
 *   else) — patients get nothing, or the wrong thing.
 * - `attention`: it goes out, but a channel the clinic switched on cannot, or messages were
 *   refused / not sent in the window.
 */
export function verdictOf(ev: NotificationEventRow, totals: EventTotals | null): Verdict {
  if (ev.audience === "STAFF_ROLE") return "off";
  if (!ev.enabled) return "off";
  const states = Object.values(ev.channelReadiness ?? {}) as ZbsReadiness[];
  if (states.length === 0 && ev.zbsReadiness) states.push(ev.zbsReadiness);
  if (states.includes("TEMPLATE_UNSUITABLE")) return "broken";
  // Every channel DISABLED is a deliberate off (e.g. patient reminders switched off) — not a fault.
  if (states.length > 0 && states.every((s) => s === "DISABLED")) return "off";
  const live = states.filter((s) => s !== "DISABLED" && s !== "CHANNEL_OFF");
  // Switched on, and no channel can carry it — including "every channel is off" (review #19566).
  if (states.length > 0 && !live.some((s) => s === "READY" || s === "UNKNOWN")) return "broken";
  if (states.some((s) => !QUIET.includes(s))) return "attention";
  if (totals && (totals.failed > 0 || totals.notSent > 0)) return "attention";
  return "ok";
}

/** A sentence a receptionist can act on, for each failure code the log records. */
export const FAILURE_LABEL: Record<string, string> = {
  MISSING_PARAM: "Mẫu cần thông tin mà tin không có",
  TEMPLATE_PARAM_MISMATCH: "Giá trị trống hoặc không khớp mẫu",
  RECIPIENT_INVALID: "Số điện thoại không hợp lệ",
  RECIPIENT_CONSENT_MISSING: "Bệnh nhân chưa đồng ý nhận liên hệ",
  RECIPIENT_EMAIL_UNAVAILABLE: "Người nhận chưa có email",
  RECIPIENT_PHONE_UNAVAILABLE: "Người nhận chưa có số điện thoại",
  NO_TEMPLATE: "Kênh chưa chọn mẫu",
  FAILED_TO_SUBMIT: "Không gửi được tới Addy Message",
  REJECTED_BY_PLATFORM: "Addy Message từ chối",
  SUPPRESSED_BY_DEPLOYMENT: "Bị chặn bởi cấu hình hệ thống",
  // #19638 / #19636 — answered by Addy Message, synchronously or by webhook.
  CHANNEL_NOT_ENABLED: "Kênh chưa được bật trên Addy Message",
  NO_PROVIDER_ACCOUNT: "Kênh chưa chọn nhà cung cấp / máy chủ mặc định",
  SUPPRESSED: "Người nhận đã chặn hoặc từng bị trả thư",
  EXPIRED: "Quá hạn trước khi gửi được",
  CHANNEL_ERROR: "Lỗi cấu hình kênh hoặc nội dung mẫu",
  ROUTE_EXHAUSTED: "Đã thử hết các bước gửi, không thành công",
  ACCOUNT_BLOCKED: "Tài khoản nhà cung cấp bị khoá hoặc hết hạn mức",
};

/** Per channel where the words differ: an e-mail is not a phone number. */
const CHANNEL_FAILURE_LABEL: Record<string, Record<string, string>> = {
  EMAIL: { RECIPIENT_INVALID: "Địa chỉ email bị máy chủ từ chối hoặc không hợp lệ" },
};

const CHANNEL_NAME: Record<string, string> = { ZNS: "Zalo", ZBS: "Zalo", SMS: "SMS", EMAIL: "Email" };

export function failureLabel(code: string, channel?: string): string {
  const text = (channel && CHANNEL_FAILURE_LABEL[channel]?.[code]) ?? FAILURE_LABEL[code] ?? code;
  return channel ? `${CHANNEL_NAME[channel] ?? channel}: ${text}` : text;
}
